/**
 * useDynamicParameters Hook
 *
 * Hook for managing dynamic parameter options that depend on connections
 * or other parameter values. Handles loading states, caching, and
 * dependency resolution.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useConnectionStore } from '@/stores/connectionStore';
import {
  fetchDynamicOptions,
  validateDependencies,
  type DynamicOption,
  type FetchDynamicOptionsRequest,
} from '@/services/dynamicDataService';
import {
  fetchDynamicListValues,
  resolveDynamicParameters,
  getDependencyParameterNames,
  getRuntimeExpressionDependencies,
  findOperationInSwagger,
} from '@/services/connectorDynamicService';
import { getBuiltinDynamicListResolver } from '@/services/builtinDynamicListRegistry';
// Side-effect import: registers portal-side resolvers (e.g. listFoundryAgents).
import '@/services/builtinDynamicListResolvers';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import { createResolverFromSwagger, type ParameterNameResolver } from '@/services/parameterNamingService';
import type { ParameterInfo, DynamicLoadStatus, DynamicValuesConfig } from '@/types/operationParameter';
import type { ReferenceKey, NodeId, ConnectionReference } from '@/types/connectionReference';
import type { DereferencedSwagger } from '@/parsers/SwaggerParser';
import { useConnectorSwagger } from '@/hooks/useConnectorSwagger';
import { useMiAudience } from '@/hooks/useMiAudience';
import { useManagedConnectionInfo } from '@/hooks/useManagedConnectionInfo';
import { isManagedApiConnector } from '@/utils/connections';
import { useAuth } from '@/auth';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';

// =============================================================================
// TYPES
// =============================================================================

export interface UseDynamicParametersOptions {
  /** Node ID for connection lookup */
  nodeId: NodeId;
  /** Operation/connector ID */
  operationId: string;
  /** Parameters that may have dynamic options */
  parameters: ParameterInfo[];
  /** Current parameter values for dependency resolution */
  parameterValues: Record<string, unknown>;
  /** Whether to auto-fetch on mount */
  autoFetch?: boolean;
}

export interface DynamicParameterState {
  /** Loading status */
  status: DynamicLoadStatus;
  /** Available options (if loaded) */
  options: DynamicOption[];
  /** Error message (if failed) */
  error?: string;
  /** Timestamp of last successful fetch */
  lastFetched?: number;
}

export interface UseDynamicParametersResult {
  /** Dynamic state for each parameter (keyed by parameter ID) */
  dynamicState: Record<string, DynamicParameterState>;
  /** Fetch options for a specific parameter */
  fetchOptionsForParameter: (parameterId: string) => Promise<void>;
  /** Refresh all dynamic parameters */
  refreshAll: () => Promise<void>;
  /** Check if a parameter has dynamic options */
  hasDynamicOptions: (parameterId: string) => boolean;
  /** Get options for a parameter (returns empty if not loaded) */
  getOptionsForParameter: (parameterId: string) => DynamicOption[];
  /** Check if any parameters are loading */
  isLoading: boolean;
  /** Check if a specific parameter is loading */
  isParameterLoading: (parameterId: string) => boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Determine if a parameter has dynamic options based on its configuration
 */
function hasDynamicConfiguration(parameter: ParameterInfo): boolean {
  // Check for dynamic data state
  if (parameter.dynamicData) {
    return true;
  }

  // Check for dynamic schema reference
  if (parameter.info?.dynamicParameterReference) {
    return true;
  }

  // Check for dependencies that would require dynamic loading
  if (parameter.info?.dependencies?.parameters?.length) {
    return true;
  }

  // Check for specific editor options that indicate dynamic loading
  const editorOptions = parameter.editorOptions;
  if (editorOptions?.dynamicOptions || editorOptions?.loadOptionsOnOpen) {
    return true;
  }

  // Check for known dynamic parameter patterns
  const dynamicPatterns = [
    'deploymentName',
    'modelName',
    'agentModelType',
    'modelConfiguration',
    'connectionName',
  ];
  if (dynamicPatterns.some((p) => parameter.parameterName.toLowerCase().includes(p.toLowerCase()))) {
    return true;
  }

  return false;
}

/**
 * Extract dependencies from a parameter
 */
function extractDependencies(parameter: ParameterInfo): string[] {
  const dependencies: string[] = [];

  if (parameter.info?.dependencies?.parameters) {
    for (const dep of parameter.info.dependencies.parameters) {
      dependencies.push(dep.parameterReference);
    }
  }

  return dependencies;
}

// =============================================================================
// HOOK
// =============================================================================

export function useDynamicParameters({
  nodeId,
  operationId,
  parameters,
  parameterValues,
  autoFetch = true,
}: UseDynamicParametersOptions): UseDynamicParametersResult {
  // State for dynamic options per parameter
  const [dynamicState, setDynamicState] = useState<Record<string, DynamicParameterState>>({});

  // Track mounted state to avoid state updates after unmount
  const mountedRef = useRef(true);
  const fetchingRef = useRef<Set<string>>(new Set());

  // Use refs for values that shouldn't trigger re-renders
  const parameterValuesRef = useRef(parameterValues);
  parameterValuesRef.current = parameterValues;

  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;

  // Get connection state
  const connectionMapping = useConnectionStore((state) => state.mapping);
  const connectionReferences = useConnectionStore((state) => state.references);

  // Get connection reference key for this node
  const connectionReferenceKey = useMemo((): ReferenceKey | undefined => {
    return connectionMapping[nodeId] ?? undefined;
  }, [connectionMapping, nodeId]);

  // Identify which parameters have dynamic options - use IDs for stable comparison
  const dynamicParameters = useMemo(() => {
    return parameters.filter(hasDynamicConfiguration);
  }, [parameters]);

  // Create a stable string of dynamic parameter IDs for effect dependencies
  const dynamicParamIds = useMemo(() => {
    return dynamicParameters.map((p) => p.id).sort().join(',');
  }, [dynamicParameters]);

  // Keep a ref for dynamic parameters to use in callbacks
  const dynamicParametersRef = useRef(dynamicParameters);
  dynamicParametersRef.current = dynamicParameters;

  // Fetch options for a single parameter
  const fetchOptionsForParameter = useCallback(
    async (parameterId: string) => {
      const parameter = parametersRef.current.find((p) => p.id === parameterId || p.parameterName === parameterId);
      if (!parameter) return;

      // Check if already fetching
      if (fetchingRef.current.has(parameterId)) return;
      fetchingRef.current.add(parameterId);

      // Update to loading state
      setDynamicState((prev) => ({
        ...prev,
        [parameterId]: {
          ...prev[parameterId],
          status: 'loading',
          options: prev[parameterId]?.options ?? [],
        },
      }));

      try {
        // Extract dependencies
        const dependencies = extractDependencies(parameter);

        // Build dependent values from current parameter values
        const dependentValues: Record<string, unknown> = {};
        for (const dep of dependencies) {
          dependentValues[dep] = parameterValuesRef.current[dep];
        }

        // Validate dependencies
        const depValidation = validateDependencies(dependentValues, dependencies);
        if (!depValidation.isValid) {
          // Dependencies not satisfied - return empty options
          if (mountedRef.current) {
            setDynamicState((prev) => ({
              ...prev,
              [parameterId]: {
                status: 'succeeded',
                options: [],
                error: `Waiting for: ${depValidation.missingDependencies.join(', ')}`,
                lastFetched: Date.now(),
              },
            }));
          }
          return;
        }

        // Build request
        const request: FetchDynamicOptionsRequest = {
          parameterId: parameter.parameterName,
          operationId,
          dependentValues,
        };

        // Add connection reference if available
        if (connectionReferenceKey) {
          request.connectionReferenceKey = connectionReferenceKey;
          const ref = connectionReferences[connectionReferenceKey];
          if (ref) {
            request.connectionReference = ref;
          }
        }

        // Fetch options
        const response = await fetchDynamicOptions(request);

        if (mountedRef.current) {
          setDynamicState((prev) => ({
            ...prev,
            [parameterId]: {
              status: 'succeeded',
              options: response.options,
              lastFetched: Date.now(),
            },
          }));
        }
      } catch (error) {
        if (mountedRef.current) {
          setDynamicState((prev) => ({
            ...prev,
            [parameterId]: {
              status: 'failed',
              options: prev[parameterId]?.options ?? [],
              error: error instanceof Error ? error.message : 'Failed to load options',
            },
          }));
        }
      } finally {
        fetchingRef.current.delete(parameterId);
      }
    },
    [operationId, connectionReferenceKey, connectionReferences]
  );

  // Refresh all dynamic parameters
  const refreshAll = useCallback(async () => {
    await Promise.all(dynamicParametersRef.current.map((p) => fetchOptionsForParameter(p.id)));
  }, [fetchOptionsForParameter]);

  // Auto-fetch on mount and when dynamic parameters change
  useEffect(() => {
    mountedRef.current = true;

    if (autoFetch && dynamicParametersRef.current.length > 0) {
      // Fetch all dynamic parameters
      for (const param of dynamicParametersRef.current) {
        fetchOptionsForParameter(param.id);
      }
    }

    return () => {
      mountedRef.current = false;
    };
  }, [autoFetch, dynamicParamIds, fetchOptionsForParameter]);

  // Re-fetch when connection changes
  useEffect(() => {
    if (connectionReferenceKey && dynamicParametersRef.current.length > 0) {
      // Connection changed - refresh dynamic parameters that depend on it
      for (const param of dynamicParametersRef.current) {
        const dependencies = extractDependencies(param);
        // If this parameter has a connection dependency or no explicit dependencies
        // (implicitly depends on connection), re-fetch
        if (dependencies.length === 0 || dependencies.some((d) => d.includes('connection'))) {
          fetchOptionsForParameter(param.id);
        }
      }
    }
  }, [connectionReferenceKey, fetchOptionsForParameter]);

  // Check if a parameter has dynamic options
  const hasDynamicOptions = useCallback(
    (parameterId: string) => {
      const param = parameters.find((p) => p.id === parameterId || p.parameterName === parameterId);
      return param ? hasDynamicConfiguration(param) : false;
    },
    [parameters]
  );

  // Get options for a parameter
  const getOptionsForParameter = useCallback(
    (parameterId: string): DynamicOption[] => {
      return dynamicState[parameterId]?.options ?? [];
    },
    [dynamicState]
  );

  // Check if any parameters are loading
  const isLoading = useMemo(() => {
    return Object.values(dynamicState).some((s) => s.status === 'loading');
  }, [dynamicState]);

  // Check if a specific parameter is loading
  const isParameterLoading = useCallback(
    (parameterId: string) => {
      return dynamicState[parameterId]?.status === 'loading';
    },
    [dynamicState]
  );

  return {
    dynamicState,
    fetchOptionsForParameter,
    refreshAll,
    hasDynamicOptions,
    getOptionsForParameter,
    isLoading,
    isParameterLoading,
  };
}

// =============================================================================
// SINGLE PARAMETER HOOK
// =============================================================================

/**
 * Hook for managing dynamic options for a single parameter
 */
export function useDynamicParameterOptions(
  parameterId: string,
  operationId: string,
  nodeId: NodeId,
  parameterValues: Record<string, unknown> = {},
  dependencies: string[] = []
): {
  options: DynamicOption[];
  isLoading: boolean;
  error?: string | undefined;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<DynamicParameterState>({
    status: 'notstarted',
    options: [],
  });

  const connectionMapping = useConnectionStore((s) => s.mapping);
  const connectionReferences = useConnectionStore((s) => s.references);
  const connectionReferenceKey = connectionMapping[nodeId];

  const fetchRef = useRef(false);
  const hasFetchedRef = useRef(false);

  // Use refs for values that change frequently
  const parameterValuesRef = useRef(parameterValues);
  parameterValuesRef.current = parameterValues;

  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const fetch = useCallback(async () => {
    if (fetchRef.current) return;
    fetchRef.current = true;

    setState((prev) => ({ ...prev, status: 'loading' }));

    try {
      // Build dependent values
      const dependentValues: Record<string, unknown> = {};
      const deps = dependenciesRef.current;
      for (const dep of deps) {
        dependentValues[dep] = parameterValuesRef.current[dep];
      }

      // Validate dependencies
      const depValidation = validateDependencies(dependentValues, deps);
      if (!depValidation.isValid) {
        setState({
          status: 'succeeded',
          options: [],
          error: `Waiting for: ${depValidation.missingDependencies.join(', ')}`,
        });
        return;
      }

      const request: FetchDynamicOptionsRequest = {
        parameterId,
        operationId,
        dependentValues,
      };

      if (connectionReferenceKey) {
        request.connectionReferenceKey = connectionReferenceKey;
        const ref = connectionReferences[connectionReferenceKey];
        if (ref) {
          request.connectionReference = ref;
        }
      }

      const response = await fetchDynamicOptions(request);
      setState({
        status: 'succeeded',
        options: response.options,
        lastFetched: Date.now(),
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to load options',
      }));
    } finally {
      fetchRef.current = false;
    }
  }, [parameterId, operationId, connectionReferenceKey, connectionReferences]);

  // Auto-fetch on mount only once
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetch();
    }
  }, [fetch]);

  return {
    options: state.options,
    isLoading: state.status === 'loading',
    error: state.error,
    refresh: fetch,
  };
}

// =============================================================================
// CONNECTOR DYNAMIC LIST HOOK
// =============================================================================

/**
 * Options for the useConnectorDynamicList hook
 */
export interface UseConnectorDynamicListOptions {
  /** Dynamic values configuration from the parameter (parsed from x-ms-dynamic-list) */
  dynamicConfig: DynamicValuesConfig;
  /** Connection reference for API calls */
  connectionReference: ConnectionReference | undefined;
  /** Connector/API ID */
  connectorId: string;
  /** Current values of all parameters for dependency resolution */
  parameterValues: Record<string, unknown>;
  /** Connector swagger spec (required for managed API connections to look up operation paths) */
  connectorSwagger?: DereferencedSwagger | null | undefined;
  /** Whether to automatically fetch when dependencies are satisfied */
  autoFetch?: boolean;
  /** Whether the editor is currently focused (to trigger fetch on open) */
  isFocused?: boolean;
}

/**
 * Result from the useConnectorDynamicList hook
 */
export interface UseConnectorDynamicListResult {
  /** Fetched options */
  options: DynamicOption[];
  /** Whether options are currently loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /**
   * Whether the most recent error was an authentication failure.
   * UI may render a dedicated re-authorize affordance when true.
   */
  isAuthError?: boolean;
  /**
   * Whether a fetch has completed successfully, even if it returned zero
   * options. Consumers should treat an empty dynamic result as authoritative
   * (render an empty list) rather than falling back to static options.
   */
  hasLoaded: boolean;
  /** Whether dependencies are satisfied */
  dependenciesSatisfied: boolean;
  /** Names of missing dependencies */
  missingDependencies: string[];
  /**
   * True when the connection reference is absent or incomplete (e.g. template
   * placeholder with empty connection.id / no connectionProperties). When true,
   * the hook skips all network calls. Consumers can render a "connect first"
   * affordance instead of an empty dropdown.
   */
  missingConnection: boolean;
  /**
   * Referenced parameters whose value is a runtime expression (e.g. an agent
   * parameter). Options can't be resolved at design time, so no fetch runs.
   */
  runtimeDependencies: string[];
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook for fetching dynamic dropdown options from connector APIs
 *
 * Uses the x-ms-dynamic-list extension parsed from swagger to call
 * the appropriate connector API and fetch dropdown options.
 *
 * Features:
 * - Automatic dependency resolution
 * - Loading states
 * - Error handling with retry capability
 * - Caching (handled by connectorDynamicService)
 *
 * @example
 * ```tsx
 * const { options, isLoading, error, refetch } = useConnectorDynamicList({
 *   dynamicConfig: parameter.info.dynamicValues,
 *   connectionReference,
 *   connectorId: 'teams',
 *   parameterValues: { postAs: 'user' },
 * });
 * ```
 */
export function useConnectorDynamicList({
  dynamicConfig,
  connectionReference,
  connectorId,
  parameterValues,
  connectorSwagger: providedSwagger,
  autoFetch = true,
  isFocused = false,
}: UseConnectorDynamicListOptions): UseConnectorDynamicListResult {
  const [options, setOptions] = useState<DynamicOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);
  // True once a fetch has completed successfully, even when it returned zero
  // options. Lets consumers treat an empty dynamic result as authoritative
  // (show an empty list) instead of falling back to static options.
  const [hasLoaded, setHasLoaded] = useState(false);
  const isManagedApi = useMemo(() => isManagedApiConnector(connectorId), [connectorId]);
  const { getOttoToken, getArmToken, getFoundryToken } = useAuth();

  // Draft vs published designer mode. Included in the fetch key so switching
  // modes re-resolves app-setting references against the correct env-settings,
  // and passed into the request so the service reads/caches the right slot.
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  // A reference can exist as a template placeholder shell (api.id set but
  // connection.id / connectionProperties empty). Calling the runtime with such
  // a reference produces 400s / MisMatchingOAuthClaims, so gate every fetch
  // path on this and surface it to callers.
  const isConnectionComplete = useMemo(
    () => isConnectionReferenceComplete(connectionReference),
    [connectionReference]
  );

  // Built-in (portal-side) dynamic-list resolver short-circuit.
  // If the operationId matches a registered builtin (e.g. `listFoundryAgents`),
  // we bypass the swagger/managed-API pipeline entirely.
  const builtinResolver = useMemo(
    () => getBuiltinDynamicListResolver(dynamicConfig.operationId),
    [dynamicConfig.operationId]
  );

  // AUTO-FETCH SWAGGER IF NOT PROVIDED
  // This makes the hook self-sufficient - it will fetch swagger automatically for managed connectors
  const shouldAutoFetchSwagger = useMemo(() => !providedSwagger && isManagedApi, [providedSwagger, isManagedApi]);
  const { data: fetchedSwaggerResult, isLoading: isSwaggerLoading } = useConnectorSwagger(
    shouldAutoFetchSwagger ? connectorId : null
  );

  // Use provided swagger or fetched swagger
  const connectorSwagger = useMemo(() => providedSwagger ?? fetchedSwaggerResult?.swagger ?? null, [providedSwagger, fetchedSwaggerResult]);

  // Returns undefined for non-MI connections; the service branches on auth type.
  const miAudience = useMiAudience(connectorId);
  // Mirrors LogicAppsUX `await getConnection(...)` — `isReady` gates fetch so
  // MI connections don't hit the bare route before classification settles.
  const { isManagedIdentity, connectionRuntimeUrl, isReady } = useManagedConnectionInfo(
    connectorId,
    connectionReference?.connection.id ?? null
  );

  // Build ParameterNameResolver for resolving x-ms-summary variations and parameterReferences
  // e.g., "PostAs" (from x-ms-summary "Post as") → "poster" (canonical param.name)
  const connectorSwaggerParser = useMemo(() => fetchedSwaggerResult?.parser ?? null, [fetchedSwaggerResult]);
  const resolver = useMemo((): ParameterNameResolver | undefined => {
    if (!connectorSwaggerParser) return undefined;
    return createResolverFromSwagger(connectorSwaggerParser);
  }, [connectorSwaggerParser]);

  // Track whether we've fetched to avoid duplicate calls
  const hasFetchedRef = useRef(false);
  // The fetch currently in flight (if any). `key` can legitimately be null
  // (e.g. service-provider connections with an empty connection.id), so an
  // explicit `inFlight` flag is used as the sentinel rather than overloading
  // null. Lets us dedup same-key fetches without blocking a different-key one.
  const inFlightRef = useRef<{ key: string | null } | null>(null);
  // Always points at the latest fetchKey so an async fetch can detect that its
  // key has gone stale (dependencies changed mid-flight) and discard its result.
  const currentFetchKeyRef = useRef<string | null>(null);

  // Extract path parameter values from _path for swagger operations
  // Path like "/v1.0/.../poster/\${encodeURIComponent('User')}/location/\${encodeURIComponent('Channel')}"
  // Also handles legacy @{...} syntax from existing workflow definitions
  const enrichedParameterValues = useMemo(() => {
    const result = { ...parameterValues };
    const pathValue = parameterValues['_path'];

    if (typeof pathValue === 'string' && (pathValue.includes('${encodeURIComponent(') || pathValue.includes('@{encodeURIComponent('))) {
      // Extract path parameter values: /paramName/\${encodeURIComponent('value')} or @{...}
      const pathParamPattern = /\/([a-zA-Z_][a-zA-Z0-9_]*)\/[@$]\{encodeURIComponent\('([^']+)'\)\}/g;
      let match;
      while ((match = pathParamPattern.exec(pathValue)) !== null) {
        const [_fullMatch, paramName, paramValue] = match;
        if (paramName && paramValue && !(paramName in result)) {
          result[paramName] = paramValue;
        }
      }
    } 


    return result;
  }, [parameterValues]);

  // Use refs for frequently changing values
  const parameterValuesRef = useRef(enrichedParameterValues);
  parameterValuesRef.current = enrichedParameterValues;

  // Calculate dependency state using ParameterNameResolver for parameter resolution
  const { dependenciesSatisfied, missingDependencies, resolvedParams } = useMemo(() => {

    // Look up the dynamic-values target operation's swagger params so the
    // resolver can distinguish real-but-unset references (e.g. SharePoint
    // `dataset` / Site Address) from connectors that misuse `{ parameter }`
    // to pass static filter literals (e.g. Teams `isDefined`). Without this,
    // an unset legitimate reference falls through `isLikelyStaticValue` and
    // gets substituted as the literal string — producing requests like
    // `/datasets/dataset/tables` that the backend rejects.
    const targetOp = connectorSwagger
      ? findOperationInSwagger(connectorSwagger, dynamicConfig.operationId)
      : undefined;
    const resolved = resolveDynamicParameters(
      dynamicConfig,
      enrichedParameterValues,
      resolver,
      targetOp?.parameters,
    );
    const satisfied = resolved !== null;
    const allDeps = getDependencyParameterNames(dynamicConfig);
    const missing: string[] = [];

    if (!satisfied) {
      for (const dep of allDeps) {
        let value = enrichedParameterValues[dep];

        // Use resolver as fallback for dependency check
        if (value === undefined && resolver) {
          const result = resolver.resolve(dep);
          if (result) {
            value = enrichedParameterValues[result.canonicalName];
          }
        }
        if (value === undefined || value === null || value === '') {
          missing.push(dep);
        }
      }
    }

    return { dependenciesSatisfied: satisfied, missingDependencies: missing, resolvedParams: resolved };
  }, [dynamicConfig, enrichedParameterValues, resolver, connectorSwagger]);

  const runtimeDependencies = useMemo(
    () => getRuntimeExpressionDependencies(dynamicConfig, resolvedParams),
    [dynamicConfig, resolvedParams],
  );
  const hasRuntimeDependencies = runtimeDependencies.length > 0;

  // Build a stable fetch key to track what we've fetched
  // This prevents infinite loops while still allowing re-fetch when dependencies change.
  // miAudience / isManagedIdentity / connectionRuntimeUrl are in the key so MI
  // connections re-fetch once detection settles. `isReady` gating below blocks
  // the first fetch entirely until the connection ARM resource has loaded.
  const fetchKey = useMemo(() => {
    if (!connectionReference?.connection?.id || !resolvedParams) return null;
    const sortedParams = JSON.stringify(resolvedParams, Object.keys(resolvedParams).sort());
    return `${connectionReference.connection.id}::${dynamicConfig.operationId}::${sortedParams}::${miAudience ?? ''}::${isManagedIdentity}::${connectionRuntimeUrl ?? ''}::${isDraftMode}`;
  }, [connectionReference?.connection?.id, dynamicConfig.operationId, resolvedParams, miAudience, isManagedIdentity, connectionRuntimeUrl, isDraftMode]);

  // Track the last successfully fetched key
  const lastFetchedKeyRef = useRef<string | null>(null);

  // Keep the latest-key ref current so in-flight fetches can detect staleness.
  currentFetchKeyRef.current = fetchKey;

  // Clear options when fetch key changes (prevents showing stale data from previous dependency values)
  useEffect(() => {
    if (fetchKey !== lastFetchedKeyRef.current) {
      setOptions([]);
      setError(null);
      setHasLoaded(false);
    }
  }, [fetchKey]);

  // Fetch function
  const fetchOptions = useCallback(async (options?: { forceRefresh?: boolean }) => {
    const forceRefresh = options?.forceRefresh === true;
    // The key this fetch is for. If the latest key diverges while we await, the
    // result is stale (dependencies changed) and must not repopulate state.
    const requestKey = fetchKey;
    const isStale = () => currentFetchKeyRef.current !== requestKey;

    // Don't fetch without a usable connection. Treat any incomplete reference
    // (no `connection.id`, no `connectionProperties`, etc.) the same as a
    // missing one — calling /dynamicInvoke with a placeholder shell only
    // produces backend errors.
    if (!connectionReference || !isConnectionComplete) {
      setOptions([]);
      setHasLoaded(false);
      return;
    }

    // `isReady` blocks fire until the connection ARM has settled (LogicAppsUX `await getConnection` parity).
    if (!isReady) {
      setHasLoaded(false);
      return;
    }

    // Don't fetch if dependencies not satisfied
    if (!resolvedParams) {
      // Dependencies not met - not an error, just waiting
      setOptions([]);
      setHasLoaded(false);
      return;
    }

    // A dependency bound to a runtime expression can't be evaluated here;
    // sending the raw expression to dynamicInvoke only produces backend errors.
    if (hasRuntimeDependencies) {
      setOptions((prev) => (prev.length === 0 ? prev : []));
      setHasLoaded(false);
      return;
    }

    // Dedup only same-key fetches. A fetch for a *different* key is allowed to
    // start concurrently; the older one's result is discarded via isStale(), so
    // it can't overwrite the cleared state after dependencies change. Comparing
    // keys (rather than presence) means a null key still dedups against itself.
    if (inFlightRef.current && inFlightRef.current.key === requestKey) return;
    // Identity token: lets the finally blocks tell "still our fetch" from "a
    // newer fetch replaced us" without relying on key equality.
    const fetchToken: { key: string | null } = { key: requestKey };
    inFlightRef.current = fetchToken;

    setIsLoading(true);
    setError(null);
    setIsAuthError(false);

    // BUILT-IN RESOLVER SHORT-CIRCUIT
    // Portal-side resolvers (e.g. listFoundryAgents) don't go through swagger or
    // managed-API token routing — they call services directly with the right
    // audience-specific token they choose themselves.
    if (builtinResolver) {
      try {
        const result = await builtinResolver({
          parameterValues: parameterValuesRef.current,
          connectionReference,
          getOttoToken,
          getArmToken,
          getFoundryToken,
        });
        if (isStale()) return;
        if (result.error) {
          setError(result.error);
          setIsAuthError(!!result.isAuthError);
          setOptions(result.options);
          setHasLoaded(false);
        } else {
          setOptions(result.options);
          setError(null);
          setIsAuthError(false);
          setHasLoaded(true);
        }
      } catch (err) {
        if (isStale()) return;
        const message = err instanceof Error ? err.message : 'Failed to load options';
        setError(message);
        setOptions([]);
        setHasLoaded(false);
      } finally {
        // Release our claim only if a newer fetch hasn't replaced it.
        if (inFlightRef.current === fetchToken) inFlightRef.current = null;
        // Only a non-stale fetch updates the completed-key bookkeeping.
        if (!isStale()) {
          lastFetchedKeyRef.current = requestKey;
          hasFetchedRef.current = true;
        }
        // Stop the spinner unless a newer fetch is still running.
        if (inFlightRef.current === null) {
          setIsLoading(false);
        }
      }
      return;
    }

    const request: Parameters<typeof fetchDynamicListValues>[0] = {
      connectionReference,
      connectorId,
      dynamicConfig,
      resolvedParameters: resolvedParams,
      accessToken: '',
      isDraftMode,
      ...(miAudience ? { miAudience } : {}),
      isManagedIdentity,
      ...(connectionRuntimeUrl ? { connectionRuntimeUrl } : {}),
      ...(forceRefresh ? { forceRefresh: true } : {}),
    };

    if (connectorSwagger) {
      request.connectorSwagger = connectorSwagger;
    } else if (isManagedApi) {
      // For managed APIs, we require swagger to resolve the operation path for dynamic list calls.
      // Release the in-flight claim and return so fetch can run again once swagger loads.
      if (inFlightRef.current === fetchToken) inFlightRef.current = null;
      setIsLoading(false);
      return;
    }
    // Service providers and other non-managed connectors don't need swagger — the
    // runtime resolves operations by operationId directly via the operationGroups path.
    // Service-provider runtime calls are routed through the Otto data plane
    // (see buildServiceProviderRuntimeUrl in ./serviceProviderRuntime), so no fqdn lookup is needed here.

    try {
      // Pick the token audience based on which endpoint fetchDynamicListValues will hit:
      //  - Managed API + modern x-ms-dynamic-list  → ARM (`management.azure.com{connectionId}/dynamicList`)
      //  - Managed API + legacy x-ms-dynamic-values → Otto control plane (`{logicEndpoint}{connectionId}/dynamicInvoke`)
      //  - Service providers                        → Otto control plane (data-plane proxy)
      // Sending the wrong audience produces `MisMatchingOAuthClaims` on the Otto endpoints
      // and 401/403 on ARM.
      const useArmToken = isManagedApi && !dynamicConfig.isLegacy;
      const accessToken = await (useArmToken ? getArmToken() : getOttoToken());

      if (!accessToken) {
        throw new Error('Unable to acquire access token');
      }
      request.accessToken = accessToken;
      const response = await fetchDynamicListValues(request);

      if (isStale()) return;
      if (response.error) {
        setError(response.error);
        setOptions([]);
        setHasLoaded(false);
      } else {
        setOptions(response.options);
        setError(null);
        setHasLoaded(true);
      }
    } catch (err) {
      if (isStale()) return;
      const message = err instanceof Error ? err.message : 'Failed to load options';
      console.error('[DynamicList] fetch threw', {
        connectorId,
        operationId: dynamicConfig.operationId,
        error: message,
      });
      setError(message);
      setOptions([]);
      setHasLoaded(false);
    } finally {
      // Release our claim (only if a newer fetch hasn't replaced it), record
      // completion for non-stale fetches (prevents infinite retry loops), and
      // stop the spinner unless a newer fetch is still running.
      if (inFlightRef.current === fetchToken) inFlightRef.current = null;
      if (!isStale()) {
        lastFetchedKeyRef.current = requestKey;
        hasFetchedRef.current = true;
      }
      if (inFlightRef.current === null) {
        setIsLoading(false);
      }
    }
  }, [isManagedApi, getOttoToken, getArmToken, getFoundryToken, connectionReference, isConnectionComplete, isReady, resolvedParams, hasRuntimeDependencies, connectorId, dynamicConfig, connectorSwagger, fetchKey, builtinResolver, miAudience, isManagedIdentity, connectionRuntimeUrl]);

  // Auto-fetch when dependencies are satisfied and we have required data
  // Only fetch if the fetch key has changed (prevents infinite loops)
  // Managed APIs additionally require swagger; service providers don't.
  // Built-in resolvers bypass swagger entirely.
  useEffect(() => {
    const swaggerReady = builtinResolver != null || !isManagedApi || !!connectorSwagger;
    if (autoFetch && dependenciesSatisfied && fetchKey && fetchKey !== lastFetchedKeyRef.current && isConnectionComplete && swaggerReady && isReady) {
      fetchOptions();
    }
  }, [autoFetch, dependenciesSatisfied, fetchKey, isConnectionComplete, connectorSwagger, isManagedApi, isReady, fetchOptions, builtinResolver]);

  // Track previous focus state to detect focus *transitions* (false → true)
  const prevFocusedRef = useRef(false);

  // Re-fetch when focused (dropdown opened) - only on focus transition
  useEffect(() => {
    const wasFocused = prevFocusedRef.current;
    prevFocusedRef.current = isFocused;

    // Only trigger on focus transition (false → true), not when already focused
    if (isFocused && !wasFocused && dependenciesSatisfied && isConnectionComplete) {
      // Reset last fetched key to allow re-fetch on focus
      lastFetchedKeyRef.current = null;
      hasFetchedRef.current = false;
      fetchOptions();
    }
  }, [isFocused, dependenciesSatisfied, isConnectionComplete, fetchOptions]);

  // Manual refetch (user-initiated). Bypass the dynamic list cache so
  // the network call actually runs.
  const refetch = useCallback(() => {
    lastFetchedKeyRef.current = null;
    hasFetchedRef.current = false;
    fetchOptions({ forceRefresh: true });
  }, [fetchOptions]);

  return {
    options,
    // Include swagger loading state in overall loading indicator
    isLoading: isLoading || (shouldAutoFetchSwagger && isSwaggerLoading),
    error,
    isAuthError,
    hasLoaded,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection: !isConnectionComplete,
    runtimeDependencies,
    refetch,
  };
}
